import React from "react";
import { TimeRuler } from "@/components/audio-editor/TimeRuler";
import { TrackContent } from "@/components/audio-editor/TrackContent";
import type { Clip } from "@/components/audio-editor/TrackContent";
import type { TrackDefinition } from "@/api/client";

export type TimelineScrollAreaProps = {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  clipDrag: { id: string } | null;
  timelineWidth: number;
  timelineDuration: number;
  pxPerSec: number;
  storyDuration: number;
  currentTime: number;
  narrationClipsWave: Clip[];
  narrationBadges: Array<{ atSec: number; n: number }>;
  tracks: TrackDefinition[];
  canAddAmbientTrack: boolean;
  canAddSfxTrack: boolean;
  buildClipsForTrack: (track: TrackDefinition) => Clip[];
  onTimelineMouseDown: (e: React.MouseEvent) => void;
  onWheel: (e: React.WheelEvent<HTMLDivElement>) => void;
};

export function TimelineScrollArea({
  scrollContainerRef,
  clipDrag,
  timelineWidth,
  timelineDuration,
  pxPerSec,
  storyDuration,
  currentTime,
  narrationClipsWave,
  narrationBadges,
  tracks,
  canAddAmbientTrack,
  canAddSfxTrack,
  buildClipsForTrack,
  onTimelineMouseDown,
  onWheel,
}: TimelineScrollAreaProps) {
  return (
    <div
      ref={scrollContainerRef}
      className="overflow-x-auto flex-1 relative timeline-scroll-container min-w-0"
      onWheel={onWheel}
      style={{ cursor: clipDrag ? "grabbing" : "default" }}
    >
      <style>{`
        .timeline-scroll-container::-webkit-scrollbar {
          height: 8px;
        }
        .timeline-scroll-container::-webkit-scrollbar-track {
          background: #f8fafc;
          border-radius: 4px;
        }
        .timeline-scroll-container::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
          border: 2px solid #f8fafc;
        }
        .timeline-scroll-container::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>

      {/* Playhead */}
      {storyDuration > 0 && (
        <div
          className="absolute top-0 bottom-0 z-10 w-[2px] bg-[#007aff] opacity-80 pointer-events-none select-none"
          style={{ left: currentTime * pxPerSec }}
        >
          <div className="absolute top-0 left-1/2 -translate-x-1/2 border-x-[5px] border-b-[6px] border-x-transparent border-b-[#007aff]" />
        </div>
      )}

      {/* Wide inner content */}
      <div style={{ width: timelineWidth }}>
        <TimeRuler
          totalDuration={timelineDuration}
          pxPerSec={pxPerSec}
          onMouseDown={onTimelineMouseDown}
        />
        <div className="border-t border-[#e2e8f0]">
          <TrackContent clips={narrationClipsWave} pxPerSec={pxPerSec} badges={narrationBadges} />
          {tracks.map((t) => (
            <TrackContent key={t.id} clips={buildClipsForTrack(t)} pxPerSec={pxPerSec} />
          ))}
          {(canAddAmbientTrack || canAddSfxTrack) && (
            <div className="h-[58px] bg-[#f8fafc] border-b border-[#e2e8f0]" />
          )}
        </div>
      </div>
    </div>
  );
}
